/**
 * Where something you made in one product can go next, in another.
 *
 * THE ARGUMENT FOR ONE CODEBASE, made visible. A briefing and a brainstorm live
 * in different products at different hosts, but on one database — so pushing a
 * briefing into a brainstorm is a write, not an integration. No webhook, no
 * sync, no contract between services. That is the thing a competitor selling
 * only one of these cannot copy, and this control is where a user meets it.
 *
 * SHOWN LOCKED, NOT HIDDEN — and this is deliberately the opposite of the rule
 * for nav sections. A whole product you did not buy is clutter in your sidebar
 * and belongs on the launcher. But a specific thing your OWN work could do, if
 * you also had the other product, is a teaching moment arriving exactly when it
 * is relevant. A Beats user who never learns their articles could become
 * brainstorm evidence has no reason to buy the second product.
 */
import { Link } from 'react-router-dom';
import { useEntitlements } from '../../contexts/EntitlementContext';
import { PRODUCT_INFO } from '../../shell/products';
import type { Product } from '../../types';

interface Props {
  /** The product this action needs. */
  product: Product;
  label: string;
  /** What running it does. Only rendered when locked — when they hold the
   *  product the button's own label is enough. */
  unlockedBy?: string;
  onClick: () => void;
  disabled?: boolean;
}

export default function HandoffControl({
  product,
  label,
  unlockedBy,
  onClick,
  disabled,
}: Props) {
  const { has, loading } = useEntitlements();

  // Say nothing until the answer is in: a control that appears locked and then
  // unlocks a moment later reads as a bug.
  if (loading) return null;

  if (has(product)) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-blue-400 dark:hover:border-blue-500 disabled:opacity-50"
      >
        {label}
      </button>
    );
  }

  const info = PRODUCT_INFO[product];
  return (
    <Link
      to="/"
      title={unlockedBy || `Available with ${info?.name ?? product}`}
      className="text-xs px-2 py-1 rounded-md border border-dashed border-gray-300 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
    >
      {label}
      <span className="ml-1.5 opacity-70">· {info?.name ?? product}</span>
    </Link>
  );
}
